import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container, Box, Typography, Grid,
    Select, MenuItem, Alert, Paper,
    FormControl, InputLabel, useTheme,
    Skeleton, Stack
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

import SummaryCard from '../components/dashboard/SummaryCard';
import TopCategoriesChart from '../components/dashboard/TopCategoriesChart';
import MonthlyCategoryStrips from '../components/dashboard/MonthlyCategoryStrips';
import TransactionDrilldownDialog from '../components/dashboard/TransactionDrilldownDialog';

export default function TransactionDashboard() {
    const { t } = useTranslation();
    const { id } = useParams();
    const { api, formatCurrency, preferredCurrency } = useAuth();
    const theme = useTheme();

    // State
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]);

    // Dialog State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState(''); // 'income' or 'spending'
    const [dialogEntries, setDialogEntries] = useState([]);
    const [dialogLoading, setDialogLoading] = useState(false);
    const [activeDrilldownFilter, setActiveDrilldownFilter] = useState(null);

    // Default to current year
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Generate an array of years (e.g., from 10 years ago to current)
    const availableYears = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 10 }, (_, i) => currentYear - i);
    }, []);

    const fetchDashboardStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [statsRes, catRes] = await Promise.all([
                api.get(`/ledger/ledgers/${id}/dashboard-stats?year=${selectedYear}`),
                api.get(`/categories/ledger/${id}`).catch(() => ({ data: [] }))
            ]);
            setStats(statsRes.data);
            if (catRes.data) setCategories(catRes.data);
        } catch (err) {
            console.error("Failed to fetch dashboard stats", err);
            setError("Unable to load performance metrics for the selected year.");
        } finally {
            setLoading(false);
        }
    }, [api, id, selectedYear]);

    useEffect(() => {
        fetchDashboardStats();
    }, [fetchDashboardStats]);

    const fetchDrilldownEntries = useCallback(async (filter) => {
        if (!filter) return;
        setDialogLoading(true);
        try {
            let startDate, endDate;
            if (filter.month) {
                const monthStr = String(filter.month).padStart(2, '0');
                const lastDay = new Date(selectedYear, filter.month, 0).getDate();
                startDate = `${selectedYear}-${monthStr}-01T00:00:00.000Z`;
                endDate = `${selectedYear}-${monthStr}-${String(lastDay).padStart(2, '0')}T23:59:59.999Z`;
            } else {
                startDate = `${selectedYear}-01-01T00:00:00.000Z`;
                endDate = `${selectedYear}-12-31T23:59:59.999Z`;
            }

            const response = await api.get(`/ledger/ledgers/${id}/entries?limit=1000&start_date=${startDate}&end_date=${endDate}&include_exchanged_amount=true`);
            const rawEntries = response.data?.items || (Array.isArray(response.data) ? response.data : []);
            let filtered = [...rawEntries];

            if (filter.type === 'income') {
                filtered = rawEntries.filter(e => {
                    const catType = e.category?.category_type;
                    if (catType === 'credit') return true;
                    if (!catType && Number(e.amount) < 0) return true;
                    return false;
                });
            } else {
                // Spending entries
                filtered = rawEntries.filter(e => {
                    const catType = e.category?.category_type;
                    if (catType === 'debit') return true;
                    if (!catType && Number(e.amount) > 0) return true;
                    return false;
                });
                if (filter.categoryName === 'Other') {
                    const topSet = new Set(filter.topCategories || []);
                    filtered = filtered.filter(e => !topSet.has(e.category?.name));
                } else if (filter.categoryName) {
                    filtered = filtered.filter(e => e.category?.name === filter.categoryName);
                }
            }

            filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
            setDialogEntries(filtered);
        } catch (err) {
            console.error("Failed to fetch detailed entries", err);
        } finally {
            setDialogLoading(false);
        }
    }, [api, id, selectedYear]);

    const handleCardClick = useCallback((type) => {
        const filter = { type };
        setActiveDrilldownFilter(filter);
        setDialogType(type);
        setDialogOpen(true);
        fetchDrilldownEntries(filter);
    }, [fetchDrilldownEntries]);

    const handleCategoryClick = useCallback((categoryInfo) => {
        const filter = {
            type: 'spending',
            ...categoryInfo
        };
        setActiveDrilldownFilter(filter);
        setDialogType('spending');
        setDialogOpen(true);
        fetchDrilldownEntries(filter);
    }, [fetchDrilldownEntries]);

    // Compute unified category color map for 1:1 color parity across charts
    const categoryColorMap = useMemo(() => {
        const categoryColors = theme.palette.chartColors || [
            theme.palette.primary.main,
            '#2563EB',
            '#7C3AED',
            '#D97706',
            '#E11D48',
            '#0891B2',
            '#C026D3',
            '#EA580C',
            '#0D9488',
            '#64748B'
        ];

        const annualTotals = {};
        if (stats?.monthly_breakdown && Array.isArray(stats.monthly_breakdown)) {
            stats.monthly_breakdown.forEach(month => {
                if (month && month.categories) {
                    month.categories.forEach(cat => {
                        annualTotals[cat.name] = (annualTotals[cat.name] || 0) + (cat.amount || 0);
                    });
                }
            });
        }
        if (stats?.top_categories && Array.isArray(stats.top_categories)) {
            stats.top_categories.forEach(cat => {
                if (!annualTotals[cat.name]) {
                    annualTotals[cat.name] = Number(cat.amount || 0);
                }
            });
        }

        const sortedCategories = Object.entries(annualTotals)
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name);

        const colorMap = {};
        let nonOtherIndex = 0;
        sortedCategories.forEach(name => {
            if (name === 'Other') {
                colorMap[name] = '#64748B';
            } else {
                colorMap[name] = categoryColors[nonOtherIndex % categoryColors.length];
                nonOtherIndex++;
            }
        });

        return colorMap;
    }, [stats, theme]);

    // Calculate chart data for MUI X Charts
    const chartData = useMemo(() => {
        if (!stats || !stats.top_categories || stats.top_categories.length === 0) return [];
        return stats.top_categories.map((cat, index) => ({
            id: index,
            value: Number(cat.amount),
            label: t(cat.name),
            color: categoryColorMap[cat.name]
        }));
    }, [stats, categoryColorMap, t]);

    const activeCurrency = stats?.preferred_currency || preferredCurrency;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 12 }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 4,
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h4" sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2.125rem' }
                    }}>
                        {t('Dashboard Overview')}
                    </Typography>
                </Box>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="year-select-label">{t('Year')}</InputLabel>
                    <Select
                        labelId="year-select-label"
                        id="year-select"
                        value={selectedYear}
                        label={t('Year')}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        sx={{ bgcolor: 'background.paper' }}
                    >
                        {availableYears.map(year => (
                            <MenuItem key={year} value={year}>{year}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            {loading ? (
                <Grid container spacing={3}>
                    {/* Top Row: 3 Summary Cards Skeleton */}
                    {[1, 2, 3].map((cardIdx) => (
                        <Grid item xs={12} sm={6} md={4} size={{ xs: 12, sm: 6, md: 4 }} key={cardIdx}>
                            <Paper
                                elevation={0}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    minHeight: { xs: 120, sm: 140 },
                                    p: { xs: 2.5, sm: 3 },
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                            >
                                <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Skeleton variant="text" width="45%" height={18} />
                                        <Skeleton variant="circular" width={28} height={28} />
                                    </Box>
                                    <Skeleton variant="text" width="70%" height={44} sx={{ my: 0.5 }} />
                                    <Skeleton variant="text" width="30%" height={18} />
                                </Box>
                            </Paper>
                        </Grid>
                    ))}

                    {/* Middle Row: Top Categories Chart Skeleton */}
                    <Grid item xs={12} size={{ xs: 12 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                width: '100%',
                                p: { xs: 2, sm: 4 },
                                minHeight: { xs: 450, sm: 350 },
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Skeleton variant="text" width="25%" height={28} />
                                <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 1 }} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'center', gap: 4, py: 2 }}>
                                <Box sx={{ position: 'relative', width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Skeleton variant="circular" width={200} height={200} />
                                </Box>
                                <Box sx={{ flex: 1, width: '100%', maxWidth: 450 }}>
                                    <Stack spacing={1.5}>
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                                    <Skeleton variant="circular" width={12} height={12} sx={{ flexShrink: 0 }} />
                                                    <Skeleton variant="text" width={`${Math.max(30, 80 - i * 10)}%`} height={20} />
                                                </Box>
                                                <Skeleton variant="text" width={55} height={20} sx={{ flexShrink: 0 }} />
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Bottom Row: Monthly Category Strips Skeleton */}
                    <Grid item xs={12} size={{ xs: 12 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                width: '100%',
                                p: { xs: 2, sm: 4 },
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Skeleton variant="text" width="30%" height={28} />
                                <Skeleton variant="text" width={100} height={20} />
                            </Box>
                            <Stack spacing={2} sx={{ mb: 4 }}>
                                {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m) => (
                                    <Box key={m} sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Box sx={{ width: 45, flexShrink: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.85rem' }}>{m}</Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, mx: 1.5 }}>
                                            <Skeleton variant="rounded" width="100%" height={24} sx={{ borderRadius: 0.5 }} />
                                        </Box>
                                        <Box sx={{ width: 80, flexShrink: 0, textAlign: 'right' }}>
                                            <Skeleton variant="text" width={60} height={20} sx={{ ml: 'auto' }} />
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5 }}>
                                        <Skeleton variant="circular" width={8} height={8} />
                                        <Skeleton variant="text" width={60} height={18} />
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            ) : stats ? (
                <Grid container spacing={3}>
                    {/* Top Row: Anchored Metrics */}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        {(() => {
                            const netIncome = Math.abs(stats.total_income) - Math.abs(stats.total_spending);
                            const isNegative = netIncome < 0;
                            return (
                                <SummaryCard
                                    variant={isNegative ? 'error' : 'dark'}
                                    title={t('Net Income')}
                                    amount={formatCurrency(netIncome, activeCurrency)}
                                    currency={activeCurrency}
                                    icon={isNegative ? <TrendingDownIcon sx={{ mr: 0.5 }} /> : <TrendingUpIcon sx={{ mr: 0.5 }} />}
                                    subtextColor="inherit"
                                />
                            );
                        })()}
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <SummaryCard
                            title={t('Total Spending')}
                            amount={formatCurrency(stats.total_spending, activeCurrency)}
                            currency={activeCurrency}
                            icon={<TrendingDownIcon sx={{ mr: 0.5 }} />}
                            subtextColor="error.main"
                            onClick={() => handleCardClick('spending')}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <SummaryCard
                            title={t('Total Income')}
                            amount={formatCurrency(stats.total_income, activeCurrency)}
                            currency={activeCurrency}
                            icon={<TrendingUpIcon sx={{ mr: 0.5 }} />}
                            subtextColor="success.main"
                            onClick={() => handleCardClick('income')}
                        />
                    </Grid>

                    {/* Bottom Row: Visualizations */}
                    <Grid item xs={12}>
                        <TopCategoriesChart
                            chartData={chartData}
                            selectedYear={selectedYear}
                            colorMap={categoryColorMap}
                            onCategoryClick={handleCategoryClick}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <MonthlyCategoryStrips
                            breakdown={stats.monthly_breakdown || []}
                            preferredCurrency={stats.preferred_currency}
                            colorMap={categoryColorMap}
                            onCategoryClick={handleCategoryClick}
                        />
                    </Grid>
                </Grid>
            ) : null}

            {/* Drilldown Dialog */}
            <TransactionDrilldownDialog
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setActiveDrilldownFilter(null);
                }}
                dialogType={dialogType}
                selectedYear={selectedYear}
                loading={dialogLoading}
                entries={dialogEntries}
                categories={categories}
                ledgerId={id}
                categoryName={activeDrilldownFilter?.categoryName}
                month={activeDrilldownFilter?.month}
                monthName={activeDrilldownFilter?.monthName}
                totalAmount={activeDrilldownFilter?.amount}
                onTransactionUpdated={() => {
                    if (activeDrilldownFilter) {
                        fetchDrilldownEntries(activeDrilldownFilter);
                    }
                    fetchDashboardStats();
                }}
            />
        </Container>
    );
}
